import config from '../config'

export type TrainNodeKind = 'project' | 'task'

export interface TrainProjectNode {
  id: number
  name: string
  parent_id?: number | null
  kind: TrainNodeKind
  description?: string | null
  created_at: string
}

export interface TrainRunSummary {
  id: number
  created_at: string
  created_by: number
  created_by_name?: string
  created_by_email?: string
  status: string
  is_released: boolean
  artifact_count: number
  top_metrics?: Record<string, any>
}

export interface TrainRunArtifact {
  id: number
  run_id: number
  file_name: string
  file_type?: string
  mime_type?: string
  file_size: number
  checksum?: string
  file_path: string
  download_url: string
}

export interface TrainRunDetail {
  id: number
  task_id: number
  dataset_node_id?: number
  dataset_job_id?: number
  dataset_artifact_path?: string
  code_repo_url?: string
  code_commit_hash?: string
  parameters: Record<string, any>
  metrics: Record<string, any>
  training_env_info: Record<string, any>
  notes?: string
  is_released: boolean
  status: string
  created_by: number
  created_at: string
  started_at?: string
  finished_at?: string
  artifact_count: number
  artifacts: TrainRunArtifact[]
}

class TrainService {
  private baseUrl = config.API_BASE_URL

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('access_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  async listProjects(): Promise<TrainProjectNode[]> {
    const resp = await fetch(`${this.baseUrl}/trains/projects`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async createProject(name: string, description?: string): Promise<{ project_id: number }> {
    const resp = await fetch(`${this.baseUrl}/trains/projects`, {
      method: 'POST', headers: this.getAuthHeaders(),
      body: JSON.stringify({ name, description })
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async getTask(taskId: number): Promise<{ id: number; project_id: number; name: string; description?: string; required_fields: Array<{ key: string; input_type: 'int'|'float'|'string'|'text' }> }> {
    const resp = await fetch(`${this.baseUrl}/trains/tasks/${taskId}`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  // 작업 카테고리 표준값 목록 (백엔드 WORK_CATEGORIES 단일 출처, 설명 포함)
  async getTaskCategories(): Promise<Array<{ name: string; description: string }>> {
    const resp = await fetch(`${this.baseUrl}/trains/task-categories`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(await resp.text())
    const data = await resp.json()
    return Array.isArray(data?.categories) ? data.categories : []
  }

  async createTask(projectId: number, name: string, description?: string, required_fields?: Array<{ key: string; input_type: 'int'|'float'|'string'|'text' }>): Promise<{ task_id: number }> {
    const payload: any = { name, description }
    if (required_fields && required_fields.length) payload.required_fields = required_fields
    const resp = await fetch(`${this.baseUrl}/trains/projects/${projectId}/tasks`, {
      method: 'POST', headers: this.getAuthHeaders(),
      body: JSON.stringify(payload)
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async updateTask(taskId: number, body: Partial<{ name: string; description: string; required_fields: Array<{ key: string; input_type: 'int'|'float'|'string'|'text' }> }>): Promise<{ id: number; project_id: number; name: string; description?: string; required_fields: Array<{ key: string; input_type: string }> }> {
    const resp = await fetch(`${this.baseUrl}/trains/tasks/${taskId}`, {
      method: 'PATCH', headers: this.getAuthHeaders(),
      body: JSON.stringify(body)
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async listRuns(taskId: number, query?: { released?: boolean; status?: string; q?: string; page?: number; size?: number; sort?: string }): Promise<TrainRunSummary[]> {
    const params = new URLSearchParams()
    if (typeof query?.released !== 'undefined') params.set('released', String(query.released))
    if (query?.status) params.set('status', query.status)
    if (query?.q) params.set('q', query.q)
    if (query?.page) params.set('page', String(query.page))
    if (query?.size) params.set('size', String(query.size))
    if (query?.sort) params.set('sort', query.sort)
    const resp = await fetch(`${this.baseUrl}/trains/tasks/${taskId}/runs?${params.toString()}`, { headers: this.getAuthHeaders() })
    if (!resp.ok) return []
    return resp.json()
  }

  async createRun(body: any): Promise<TrainRunDetail> {
    const payload = {
      task_id: body.task_id ?? body.taskId,
      dataset_node_id: body.dataset_node_id ?? body.datasetNodeId,
      dataset_job_id: body.dataset_job_id ?? body.datasetJobId,
      dataset_artifact_path: body.dataset_artifact_path ?? body.datasetArtifactPath,
      code_repo_url: body.code_repo_url ?? body.codeRepoUrl,
      code_commit_hash: body.code_commit_hash ?? body.codeCommitHash,
      parameters: body.parameters || {},
      metrics: body.metrics || {},
      training_env_info: body.training_env_info ?? body.trainingEnvInfo ?? {},
      notes: body.notes,
      is_released: body.is_released ?? body.isReleased ?? false,
      status: body.status || 'completed',
      started_at: body.started_at ?? body.startedAt,
      finished_at: body.finished_at ?? body.finishedAt,
    }
    const resp = await fetch(`${this.baseUrl}/trains/runs`, { method: 'POST', headers: this.getAuthHeaders(), body: JSON.stringify(payload) })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async getRun(runId: number): Promise<TrainRunDetail> {
    const resp = await fetch(`${this.baseUrl}/trains/runs/${runId}`, { headers: this.getAuthHeaders() })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async updateRun(runId: number, body: Partial<{ is_released: boolean; status: string; metrics: any; notes: string }>): Promise<void> {
    const payload: any = {}
    if (typeof body.is_released !== 'undefined') payload.is_released = body.is_released
    if (body.status) payload.status = body.status
    if (typeof body.metrics !== 'undefined') payload.metrics = body.metrics
    if (typeof body.notes !== 'undefined') payload.notes = body.notes
    const resp = await fetch(`${this.baseUrl}/trains/runs/${runId}`, { method: 'PATCH', headers: this.getAuthHeaders(), body: JSON.stringify(payload) })
    if (!resp.ok) throw new Error(await resp.text())
  }

  async listArtifacts(runId: number): Promise<TrainRunArtifact[]> {
    const resp = await fetch(`${this.baseUrl}/trains/runs/${runId}/artifacts`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } as any })
    if (!resp.ok) return []
    return resp.json()
  }

  async uploadArtifacts(runId: number, files: File[], types?: string[]): Promise<TrainRunArtifact[]> {
    const fd = new FormData()
    for (const f of files) fd.append('files', f)
    if (types) for (const t of types) fd.append('types', t)
    const resp = await fetch(`${this.baseUrl}/trains/runs/${runId}/artifacts`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } as any,
      body: fd,
    })
    if (!resp.ok) throw new Error(await resp.text())
    return resp.json()
  }

  async downloadArtifact(artifactId: number, suggestedFilename?: string): Promise<void> {
    const resp = await fetch(`${this.baseUrl}/trains/artifacts/${artifactId}/download`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` } as any })
    if (!resp.ok) throw new Error('다운로드 실패')
    // 파일명 파싱 (Content-Disposition)
    const cd = resp.headers.get('Content-Disposition') || ''
    let filename = suggestedFilename || `artifact_${artifactId}`
    try {
      const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd)
      if (m) {
        filename = decodeURIComponent(m[1] || m[2] || filename)
      }
    } catch {}
    const blob = await resp.blob()
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }
}

export default new TrainService()


